import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import {
  allSdgAlignmentLevels,
  allSdgIds,
  ByAlignmentLevel,
  BySdg,
  SdgAlignment,
  SdgAlignmentValence,
  SdgId,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { useHasAccessToFeature, useIsTeam } from '@upright/ui/common/generic/hooks';
import { TabletOrWiderOnly } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { useSdgAlignmentsContext } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/context';
import { SdgDrilldownModal } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { SdgRow } from './sdg-row';

interface Selection {
  sdg: SdgId;
  valence: SdgAlignmentValence;
}

export const AllSdgAlignments = () => {
  const [selection, setSelection] = useState<Selection | undefined>(undefined);
  const ctx = useSdgAlignmentsContext();
  const { resultId, entityId } = ctx;
  const isTeam = useIsTeam();
  const coverageClass = ctx.coverageClass;
  const hasProductViewAccess = useHasAccessToFeature(FeatureId.PRODUCT_VIEW);
  const drilldownData = selection && ctx.sdgAlignmentsRequest.data && ctx.sdgAlignmentsRequest.data[selection.sdg];
  const { release } = useSelector(getVersionAndRelease);
  const sdgIdsToShow = useGetSdgsToShow();
  const pushGtmEvent = usePushGtmEvent();

  if (release === null) {
    return <></>;
  }
  return (
    <Root>
      {sdgIdsToShow.map(sdgId => (
        <SdgRow
          key={`sdg-row-${sdgId}`}
          {...{ sdgId }}
          onClick={(valence, sdg) => {
            pushGtmEvent({
              event: CustomGtmEvent.IMPACT_PROFILE_SDG_DRILLDOWN_OPEN,
              data: {
                valence,
                sdgId,
              },
            });
            setSelection({ valence, sdg });
          }}
        />
      ))}
      {
        <TabletOrWiderOnly>
          <SdgDrilldownModal
            unitsMode={ctx.viewOptions.unitsMode}
            sdgId={selection?.sdg}
            valence={selection?.valence}
            data={drilldownData}
            release={release}
            resultId={resultId}
            entityId={entityId}
            isOpen={!!drilldownData}
            onClose={() => {
              setSelection(undefined);
            }}
            entityType={ctx.entityType}
            hasProductViewAccess={hasProductViewAccess}
            coverageClass={coverageClass}
            isTeamUser={isTeam}
            accessLevel={ctx.entity?.accessLevel}
          />
        </TabletOrWiderOnly>
      }
    </Root>
  );
};

const Root = styled.div``;

function useGetSdgsToShow() {
  const context = useSdgAlignmentsContext();
  switch (context.viewOptions.sdgIdsToShow) {
    case 'nonEmpty':
      return getNonEmptySdgs(context.sdgAlignmentsRequest.data);
    default:
      break;
  }

  return allSdgIds;
}

function getNonEmptySdgs(sdgData: Partial<BySdg<ByAlignmentLevel<SdgAlignment>>> | undefined): SdgId[] {
  if (!sdgData) return allSdgIds;
  const filteredIds = allSdgIds.filter(sdgId => {
    const dataOfSdgId = sdgData[sdgId];
    if (!dataOfSdgId) return false;
    const alignmentLevelsWithData = allSdgAlignmentLevels.filter(alignmentLevel => {
      return !!dataOfSdgId[alignmentLevel]?.alignment;
    });
    return !!alignmentLevelsWithData.length;
  });
  return filteredIds;
}
